import { cardFactory } from '@liveauctioneers/hammer-ui-core/card/factory';
import { CompleteAccountModalContext, ModalContext, openReviewsModal } from '@/redux/modules/modal';
import { createFavoriteButton } from '@liveauctioneers/hammer-ui-core/card/components/createFavoriteButton';
import { createImage } from '@liveauctioneers/hammer-ui-core/card/components/createImage';
import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import { EventContext } from '@/redux/modules/analytics';
import { getItemUrl, getSellerUrl } from '@/utils/urls';
import { isItemSaved } from '@/redux/modules/item/saved/savedItems.selectors';
import { ItemSummary } from '@/types/item/ItemSummary';
import { StarRating } from '@liveauctioneers/hammer-ui-core/starRating';
import { SubtleLink, SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { toggleItemSave } from '@/redux/modules/item/saved/savedItems.actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import { useTrackAuctionHouseClickPriceResultsPage } from '@/hooks/kameleoon/tracking/useTrackAuctionHouseClickPriceResultsPage';
import {
    useTrackItemCardClickedPriceResults,
    useTrackSavedPriceResultsItem,
} from '@/hooks/kameleoon/tracking/useTrackPriceResultsPage';
import { useTrackItemSaved } from '@/hooks/kameleoon/tracking/useTrackItemSaved';
import { useTrackReviewsModalViewedPriceResultsPage } from '@/hooks/kameleoon/tracking/useTrackReviewsModalViewedPriceResultsPage';
import ItemCardImage from '@/components/GridItemCard/Card/components/ItemCardImage/ItemCardImage';
import ItemCardPriceRow from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';

const MAX_REVIEW_COUNT = 1000;

export const UpcomingSalesItemCard = cardFactory<ItemSummary>({
    components: [
        createFavoriteButton({
            useIsFavorited: ({ itemId }) => {
                const isFavorite = useAppSelector((state) => isItemSaved(state, itemId));
                return isFavorite;
            },
            useSetFavorited: ({ itemId }) => {
                const trackSavedPriceResultsItem = useTrackSavedPriceResultsItem();
                const trackItemSaved = useTrackItemSaved();
                const dispatch = useAppDispatch();
                const isFavorite = useAppSelector((state) => isItemSaved(state, itemId));
                const verifyLogin = useRequireLogin();
                const location = useLocation();

                const modalContext = useMemo<ModalContext>(
                    () => ({
                        eventContext: EventContext.saveItem,
                        id: itemId,
                        itemId,
                        type: CompleteAccountModalContext.SaveItem,
                    }),
                    [itemId]
                );

                const onToggleSave = useCallback(() => {
                    if (!isFavorite) {
                        trackItemSaved();
                        trackSavedPriceResultsItem();
                    }

                    dispatch(toggleItemSave({ canHaveEasyShippingTag: false, itemId, location }));
                }, [dispatch, isFavorite, itemId, location, trackItemSaved, trackSavedPriceResultsItem]);

                return () => {
                    verifyLogin({
                        modalContext,
                        requireLogin: true,
                        runFunction: () => onToggleSave(),
                    });
                };
            },
        }),

        createImage<ItemSummary>({
            ImageComponent: ({ itemId }) => {
                const trackItemCardClicked = useTrackItemCardClickedPriceResults();

                return (
                    <ItemCardImage
                        className="aspect-square h-fit max-h-[381px] max-w-[381px]"
                        itemId={itemId}
                        onNavigate={() => trackItemCardClicked()}
                    />
                );
            },
        }),
        { spacerPx: 8 },
        createInfo({
            className: 'line-clamp-0',
            useText: ({ itemId, title }) => {
                const trackItemCardClicked = useTrackItemCardClickedPriceResults();

                return (
                    <SubtleMonochromeLink
                        className="line-clamp-2"
                        href={getItemUrl(itemId, title)}
                        onClick={() => trackItemCardClicked()}
                    >
                        {title}
                    </SubtleMonochromeLink>
                );
            },
        }),
        { spacerPx: 2 },
        createInfo({
            className: 'line-clamp-0',
            useText: ({ houseRating, houseReviewCount, sellerId, sellerName }) => {
                const dispatch = useAppDispatch();
                const trackReviewsModalViewedPriceResults = useTrackReviewsModalViewedPriceResultsPage();
                const trackAuctionHouseClickPriceResults = useTrackAuctionHouseClickPriceResultsPage();

                const handleRatingClick = useCallback(() => {
                    trackAuctionHouseClickPriceResults();
                    trackReviewsModalViewedPriceResults();
                    dispatch(openReviewsModal(sellerId));
                }, [dispatch, sellerId, trackAuctionHouseClickPriceResults, trackReviewsModalViewedPriceResults]);

                const trailingText = useMemo(() => {
                    if (houseReviewCount > MAX_REVIEW_COUNT) {
                        const countInThousands = Math.floor(houseReviewCount / 100) / 10;
                        return `${countInThousands}k`;
                    }
                    return houseReviewCount?.toString();
                }, [houseReviewCount]);

                return (
                    <div className="grid grid-cols-[1fr_min-content] gap-2xs">
                        <SubtleLink
                            className="truncate text-link-secondary"
                            href={getSellerUrl(sellerId, sellerName)}
                            size="sm"
                        >
                            {sellerName}
                        </SubtleLink>
                        {houseReviewCount !== 0 && (
                            <StarRating
                                averageRating={houseRating}
                                compact
                                onClick={handleRatingClick}
                                trailingText={trailingText}
                            />
                        )}
                    </div>
                );
            },
        }),
        { spacerPx: 8 },

        createInfo({
            className: 'line-clamp-0',
            useText: ({ bidCount, itemId }) => {
                const trackItemCardClicked = useTrackItemCardClickedPriceResults();
                const [linkHover, setLinkHover] = useState(false);

                const enterLinkHoverState = useCallback(() => {
                    setLinkHover(true);
                }, []);

                const leaveLinkHoverState = useCallback(() => {
                    setLinkHover(false);
                }, []);

                return (
                    <ItemCardPriceRow
                        isHovered={linkHover}
                        itemId={itemId}
                        onMouseEnter={enterLinkHoverState}
                        onMouseLeave={leaveLinkHoverState}
                        onNavigate={() => trackItemCardClicked()}
                        showBidCount={Boolean(bidCount)}
                    />
                );
            },
        }),

        { spacerPx: 2 },
        createInfo({
            className: 'line-clamp-0',
            useText: ({ itemId }) => {
                return (
                    <ItemCardShortDate
                        allowBidding
                        forceDate
                        itemId={itemId}
                    />
                );
            },
        }),
    ],

    name: 'UpcomingSalesItemCard',
});
